/* Architect WORKSPACE — the standalone white dashboard (mirrors the
   original SPA's chrome: LATOAI · ARCHITECT top bar + user chip). This
   is a full-screen surface, NOT a tab inside the dark admin: architect
   accounts land here right after login; root (and developers holding
   architect:use) switch into it from the workspace chooser / header. */

import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, LayoutGrid, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import ArchitectPanel from './ArchitectPanel';
import { TopbarActionsProvider, TopbarTitleProvider } from './topbar';
import { WorkspaceDraftGuardProvider, useWorkspaceLeaveGuard } from './useWorkspaceDraftGuard';
import './architect.css';
import './workspace-skin.css';
import './lato-ai.css';
import './workspace-panes.css';
import './stage-decision.css';
import './wise-workspace.css';
import './wise-stage-chrome.css';
import './wise-monthly.css';
import './wise-account-menu.css';
import './wise-topbar-title.css';

interface Props {
  // Present when the viewer also has a main-admin home to go back to
  // (root / developer). Confined architect accounts get no exit.
  onExit?: () => void;
  exitLabel?: string;
}

export default function ArchitectWorkspace({ onExit, exitLabel }: Props) {
  return <WorkspaceDraftGuardProvider>
    <ArchitectWorkspaceContent onExit={onExit} exitLabel={exitLabel} />
  </WorkspaceDraftGuardProvider>;
}

function ArchitectWorkspaceContent({ onExit, exitLabel }: Props) {
  const { user, logout } = useAuth();
  const requestLeave = useWorkspaceLeaveGuard();
  // Page-level actions a page registers into the bar (see topbar.tsx).
  const [actions, setActions] = useState<React.ReactNode>(null);
  const [pageTitle, setPageTitle] = useState<React.ReactNode>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRoot = useRef<HTMLDivElement>(null);
  const accountTrigger = useRef<HTMLButtonElement>(null);
  const openFocus = useRef<'first' | 'last'>('first');
  const accountMenuId = useId();
  const accountName = user?.displayName || user?.username || 'Account';
  const initials = accountName.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'A';

  useEffect(() => {
    if (!accountOpen) return;
    const items = accountRoot.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || [];
    (openFocus.current === 'last' ? items[items.length - 1] : items[0])?.focus();
    const outside = (event: PointerEvent) => {
      if (!accountRoot.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setAccountOpen(false);
      accountTrigger.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [accountOpen]);

  const accountAction = (action: () => void | Promise<unknown>) => {
    setAccountOpen(false);
    accountTrigger.current?.focus();
    requestLeave(action);
  };

  // Full-light surface: suppress the dark app's film-grain overlay and
  // radial-glow body background while this workspace is on screen.
  useEffect(() => {
    document.body.classList.add('arch-light');
    return () => document.body.classList.remove('arch-light');
  }, []);

  return (
    <div className="architect-scope architect-workspace lato-ui-kit">
      <div className={'arch-topbar' + (pageTitle ? ' has-page-title' : '')}>
        <div className="arch-topbar-left">
          <div className="arch-topbar-brand">
            LATO<span>AI</span> · ARCHITECT
          </div>
          {pageTitle ? <div className="arch-topbar-title">{pageTitle}</div> : null}
        </div>
        <div className="arch-topbar-right">
          {actions}
          <div ref={accountRoot} className="arch-account" onBlur={event => {
            if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setAccountOpen(false);
          }}>
            <button ref={accountTrigger} type="button" className="arch-account-trigger" title={user?.email}
              aria-label={`Account menu for ${accountName}`} aria-haspopup="menu" aria-expanded={accountOpen} aria-controls={accountMenuId}
              onClick={() => { openFocus.current = 'first'; setAccountOpen(value => !value); }}
              onKeyDown={event => {
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
                event.preventDefault();
                openFocus.current = event.key === 'ArrowUp' ? 'last' : 'first';
                setAccountOpen(true);
              }}>
              <span className="arch-account-avatar" aria-hidden="true">{initials}</span>
              <span className="arch-account-name">{accountName}</span>
              <ChevronDown size={15} aria-hidden="true" />
            </button>
            {accountOpen ? <div id={accountMenuId} className="arch-account-menu" role="menu" aria-label="Account"
              onKeyDown={event => {
                if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
                const current = items.indexOf(document.activeElement as HTMLButtonElement);
                const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                items[next]?.focus();
              }}>
              <div className="arch-account-details" role="presentation"><strong>{accountName}</strong>{user?.email ? <span>{user.email}</span> : null}</div>
              {onExit ? <button type="button" role="menuitem" onClick={() => accountAction(onExit)}><LayoutGrid size={17} aria-hidden="true" /><span>{exitLabel || 'Developers ↗'}</span></button> : null}
              <button type="button" role="menuitem" onClick={() => accountAction(logout)}><LogOut size={17} aria-hidden="true" /><span>Log out</span></button>
            </div> : null}
          </div>
        </div>
      </div>
      <TopbarActionsProvider value={setActions}>
        <TopbarTitleProvider value={setPageTitle}>
          <ArchitectPanel />
        </TopbarTitleProvider>
      </TopbarActionsProvider>
    </div>
  );
}
